import { createAction } from "@logicsoftware/redux-toolkit";
import { normalize } from "normalizr";
import { call, put, select, takeEvery, takeLeading } from "redux-saga/effects";
import { appUrls } from "~/EasyProjectsCommon/AppUrls";
import { OAuthClient } from "~/EasyProjectsCommon/OAuthClient";
import { NamedItem } from "~/sharedComponents/list";
import { TeamInfo, TeamsCache } from "../../EasyProjects/ActivityList/Cache/TeamsCache";
import { Entities, Team } from "../../normalizr/entities";
import { teamsSchema } from "../../normalizr/teamsSchema";
import { createEntityReducer } from "../Entities/reducers";
import { doReconstructEntities, doUpdateEntities } from "../Middleware/apiMiddleware";
import { callApi } from "../Sagas/effects/callApi";
import { ActionsTypes } from "./actionTypes";
import { AvailableUsersState, LoadTeamsRequestPayload } from "./types";

export type LoadTeamsRequestAction = ReturnType<typeof loadTeamsRequest>;

export const loadTeamsRequest = (payload: LoadTeamsRequestPayload = {}) =>
    ({ type: ActionsTypes.LOAD_TEAMS, ...payload } as const);
export const reloadTeamsRequest = () => ({ type: ActionsTypes.LOAD_TEAMS, forceReload: true });

export const getAvailableTeams = (state: Partial<AvailableUsersState>) => {
    const teams = state.availableUsers && state.availableUsers.teams;
    return teams || null;
};
export const getTeamsLoaded = (state: Partial<AvailableUsersState>) => !!getAvailableTeams(state);

export const teamsReducer = createEntityReducer(teamsSchema[0].key, {
    initState: null,
    ignoreCleanEntities: true,
});

export function* loadTeamsSaga({ beforeRequestAction, afterRequestAction, forceReload }: LoadTeamsRequestAction) {
    const teams: Entities<Team> = yield select(getAvailableTeams);
    if (teams && !forceReload) {
        return;
    }

    if (beforeRequestAction) {
        yield put(beforeRequestAction());
    }

    const cacheTeams: TeamInfo[] = yield TeamsCache.inst.getData();
    const teamEntities = cacheTeams.map(
        ({ UserIds, ...restProps }) =>
            ({
                ...restProps,
                Users: UserIds,
            } as Team)
    );

    const normalized = normalize(teamEntities, teamsSchema);
    yield put((forceReload ? doReconstructEntities : doUpdateEntities)(normalized.entities, teamsSchema[0].key));

    if (afterRequestAction) {
        yield put(afterRequestAction());
    }
}

export function* watchLoadTeamsRequestSaga() {
    yield takeLeading(ActionsTypes.LOAD_TEAMS, loadTeamsSaga);
    yield takeEvery(deleteTeam, handleDeleteTeam);
    yield takeEvery(createTeam, handleCreateTeam);
}

export const deleteTeam = createAction(ActionsTypes.DELETE_TEAM, (id: number) => ({ payload: { id } }));
function* handleDeleteTeam({ payload: { id } }: ReturnType<typeof deleteTeam>) {
    const teams: Entities<Team> = yield select(getAvailableTeams);
    const result = Object.values(teams).filter(team => team.TeamId !== id);

    const { entities } = normalize(result, teamsSchema);
    yield put(doReconstructEntities(entities, teamsSchema[0].key));

    yield callApi(({ ajaxOptions }) => ep.common.postJson(appUrls.users.deleteTeam, { id }, null, ajaxOptions));
    TeamsCache.invalidate();
}

export const createTeam = createAction(
    ActionsTypes.CREATE_TEAM,
    (name: string, callback: (item: NamedItem) => void) => ({ payload: { name, callback } })
);

function* handleCreateTeam({ payload: { name, callback } }: ReturnType<typeof createTeam>) {
    const response: { TeamId: number; Name: string } = yield callApi(({ ajaxOptions }) =>
        OAuthClient.postJson(appUrls.usermanagement.teams.createTeam, { Name: name }, ajaxOptions)
    );

    const { entities } = normalize([{ ...response, Users: [] }], teamsSchema);
    yield put(doUpdateEntities(entities, teamsSchema[0].key));
    yield call(() => callback({ value: response.TeamId, name: response.Name }));
    TeamsCache.invalidate();
}
